/* One recommended rule, and every action a person can take on it.
 *
 * **The row reads as a sentence first.** "CUSTOMER_SK must always have a value" is what a steward
 * is deciding about; the rule kind (`not_null`) stays beside it as a small tag for whoever wants the
 * technical name. The reason and its evidence sit next to it in plain words.
 *
 * **Apply offers the rules the library already has before it offers to write a new one.** An active
 * rule was already approved by two people, so serving a recommendation with it starts monitoring
 * on the next run with no new approval — and writing a duplicate of a rule the tenant already owns
 * is the outcome a steward least wants. A new rule is still one click away, and it still starts as
 * a draft that two other people must approve.
 *
 * Each row owns its own mutations, so a second row's failure never renders under the first, and a
 * busy row disables only its own buttons. The server is idempotent on the recommendation, so a
 * double-click that slipped past the disabled state still produces one rule.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';

import { LatestViolationFix } from '@/features/rules';
import { CheckboxField, TextField } from '@/shared/ui/Field';
import { SCREEN_PATHS } from '@/shared/routes';

import {
  useApplySuggestion,
  useBindSuggestion,
  useDismissSuggestion,
  useMatchingRules,
} from '../api/rulegen';
import type { ExistingRule, Suggestion } from '../api/rulegen';
import {
  STAGE_TONE,
  columnsLabel,
  confidenceBand,
  confidencePercent,
  coverageKey,
  evidenceItems,
  freeSeverities,
  headlineOf,
  parameterText,
  stageOf,
} from '../model/presentation';

type Mode = 'idle' | 'apply' | 'dismiss';

const SEVERITIES = ['info', 'warning', 'error', 'blocker'] as const;
type SeverityChoice = (typeof SEVERITIES)[number];

/** The number of table columns the editor rows span. */
const COLUMN_COUNT = 4;

export function RecommendationRow({
  item,
  datasetId,
  canApply,
  editable,
  onOpenForm,
  openOnArrival = false,
}: {
  readonly item: Suggestion;
  readonly datasetId: string;
  readonly canApply: boolean;
  readonly editable: readonly string[];
  /** The pre-lifecycle path: open the rule form prefilled. Offered only when apply is not wired. */
  readonly onOpenForm: (item: Suggestion) => void;
  /** A link asked for this recommendation: open its apply panel and bring it into view. With no
   * approved rule fitting, that panel opens on "Create a new rule" — the draft path. */
  readonly openOnArrival?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const isAi = item.source === 'model_proposed';
  const stage = stageOf(item);
  const band = confidenceBand(item.confidence);
  const headline = headlineOf(item);
  const evidence = evidenceItems(item.evidence);
  const hasParameters = Object.keys(item.parameters).length > 0;
  const [mode, setMode] = useState<Mode>(
    openOnArrival && canApply && item.status === 'pending' ? 'apply' : 'idle',
  );
  const arrival = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    const row = arrival.current;
    /* `scrollIntoView` is absent in jsdom, so it is called only where the element has it. */
    if (openOnArrival && row !== null && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'center' });
    }
  }, [openOnArrival]);
  const [reason, setReason] = useState('');
  const [severity, setSeverity] = useState<SeverityChoice>('warning');

  const bind = useBindSuggestion();
  const dismiss = useDismissSuggestion();
  const busy = bind.isPending || dismiss.isPending;
  const failure = bind.error ?? dismiss.error;

  const ruleLink =
    item.rule === null ? null : generatePath(SCREEN_PATHS.rule, { ruleId: item.rule.id });

  return (
    <>
      <tr
        ref={arrival}
        className={item.status === 'pending' ? undefined : 'lq-sg__row--decided'}
      >
        <td className="lq-sg__rec">
          <span className="lq-sg__headline">{t(headline.key, headline.values)}</span>
          <span className="lq-sg__tags">
            <code className="lq-sg__tag">{item.kind}</code>
            <span className="lq-sg__tag lq-sg__tag--soft">
              {t(`suggestions.filter.category.${item.category}`)}
            </span>
          </span>
          {item.needs_input.length > 0 ? (
            <span className="lq-sg__aside">
              <span className="lq-sg__tag lq-sg__tag--template">
                {t('suggestions.row.template')}
              </span>{' '}
              {t('suggestions.row.templateHint', { fields: item.needs_input.join(', ') })}
            </span>
          ) : (
            hasParameters && (
              <span className="lq-sg__param">{parameterText(item.parameters)}</span>
            )
          )}
          {isAi && <span className="lq-sg__aside">{t('suggestions.row.youSupply')}</span>}
        </td>
        <td className="lq-sg__why">
          <span>{item.rationale}</span>
          {evidence.length > 0 && (
            <span className="lq-sg__evidence">
              {evidence.map((entry) => (
                <span className="lq-sg__evidence-item" key={entry.key}>
                  {t(entry.key, { value: entry.value })}
                </span>
              ))}
            </span>
          )}
          {isAi && <span className="lq-sg__aside">{t('suggestions.row.hostWrote')}</span>}
          {item.origin === 'hybrid' && (
            <span className="lq-sg__aside">{t('suggestions.row.corroborated')}</span>
          )}
        </td>
        <td className="lq-sg__num">
          {/* The band in words first, so the colour is never the only signal. */}
          <span
            className={`lq-sg__conf lq-sg__conf--${band}`}
            title={t('suggestions.confidence.hint')}
          >
            <span className="lq-sg__conf-band">
              {t(`suggestions.confidence.${band}`)}
            </span>
            {confidencePercent(item.confidence)}
          </span>
        </td>
        <td className="lq-sg__num lq-sg__act-cell">
          <span
            className={`lq-sg__chip lq-sg__chip--sm lq-sg__chip--${STAGE_TONE[stage]}`}
            title={item.dismiss_reason ?? t(`suggestions.stage.${stage}.note`)}
          >
            <span
              className={`lq-sg__dot${stage === 'monitoring' ? ' lq-sg__dot--filled' : ''}`}
            />
            {t(`suggestions.stage.${stage}`, {
              collected: item.rule?.approvals_collected ?? 0,
              required: item.rule?.approvals_required ?? 2,
            })}
          </span>

          {canApply && (stage === 'open' || stage === 'dismissed') && mode === 'idle' && (
            <span className="lq-sg__actions">
              <button
                className="lq-sg__act"
                disabled={busy}
                onClick={() => {
                  setMode('apply');
                }}
                title={t('suggestions.act.applyHint')}
                type="button"
              >
                {t(
                  stage === 'dismissed'
                    ? 'suggestions.act.applyAnyway'
                    : 'suggestions.act.apply',
                )}
              </button>
              {stage === 'open' && (
                <button
                  className="lq-sg__act lq-sg__act--quiet"
                  disabled={busy}
                  onClick={() => {
                    setMode('dismiss');
                  }}
                  type="button"
                >
                  {t('suggestions.act.dismiss')}
                </button>
              )}
            </span>
          )}

          {!canApply && stage === 'open' && (
            <button
              className="lq-sg__act"
              onClick={() => {
                onOpenForm(item);
              }}
              title={t('suggestions.act.hint')}
              type="button"
            >
              {t('suggestions.act.create')}
            </button>
          )}

          {canApply && stage === 'ready_to_bind' && (
            <span className="lq-sg__actions">
              <select
                aria-label={t('suggestions.bind.severity')}
                className="lq-input lq-sg__severity"
                disabled={busy}
                onChange={(event) => {
                  setSeverity(event.target.value as SeverityChoice);
                }}
                value={severity}
              >
                {SEVERITIES.map((choice) => (
                  <option key={choice} value={choice}>
                    {t(`suggestions.bind.severity.${choice}`)}
                  </option>
                ))}
              </select>
              <button
                className="lq-sg__act"
                disabled={busy}
                onClick={() => {
                  bind.mutate({ suggestionId: item.id, datasetId, severity });
                }}
                title={t('suggestions.bind.hint')}
                type="button"
              >
                {t(bind.isPending ? 'suggestions.bind.binding' : 'suggestions.bind.go')}
              </button>
            </span>
          )}

          {ruleLink !== null && stage !== 'rule_missing' && (
            <Link className="lq-sg__rule-link" to={ruleLink}>
              {t('suggestions.act.openRule')}
            </Link>
          )}
          {/* Once the rule is bound it runs on this table, and its violations have rows to fix.
              Offered only then: before binding there is no run and so nothing to find. */}
          {stage === 'monitoring' && item.rule !== null && (
            <LatestViolationFix
              className="lq-sg__act lq-sg__act--quiet"
              datasetId={datasetId}
              ruleId={item.rule.id}
            />
          )}
          {isAi && stage === 'open' && (
            <span className="lq-sg__review">{t('suggestions.act.review')}</span>
          )}
          {failure !== null && (
            <span className="lq-sg__row-error" role="alert">
              {failure.message}
            </span>
          )}
        </td>
      </tr>

      {mode === 'apply' && (
        <tr className="lq-sg__editor-row">
          <td colSpan={COLUMN_COUNT}>
            <ApplyPanel
              datasetId={datasetId}
              editable={editable}
              headline={t(headline.key, headline.values)}
              item={item}
              onClose={() => {
                setMode('idle');
              }}
            />
          </td>
        </tr>
      )}

      {mode === 'dismiss' && (
        <tr className="lq-sg__editor-row">
          <td colSpan={COLUMN_COUNT}>
            <form
              aria-label={t('suggestions.dismiss.form', { kind: item.kind })}
              className="lq-sg__editor"
              onSubmit={(event) => {
                event.preventDefault();
                dismiss.mutate(
                  { suggestionId: item.id, datasetId, reason },
                  {
                    onSuccess: () => {
                      setMode('idle');
                    },
                  },
                );
              }}
            >
              <TextField
                label={t('suggestions.dismiss.reason')}
                hint={t('suggestions.dismiss.reasonHint')}
                maxLength={500}
                onChange={setReason}
                required
                value={reason}
              />
              <div className="lq-sg__editor-actions">
                <button
                  className="lq-sg__go"
                  disabled={dismiss.isPending || reason.trim() === ''}
                  type="submit"
                >
                  {t('suggestions.dismiss.submit')}
                </button>
                <button
                  className="lq-sg__act lq-sg__act--quiet"
                  onClick={() => {
                    dismiss.reset();
                    setMode('idle');
                  }}
                  type="button"
                >
                  {t('suggestions.act.cancel')}
                </button>
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

type Path = 'existing' | 'new';

/** The apply panel: reuse an approved rule, or write a new one.
 *
 * **Which path is preselected follows the library**, not a fixed default: when an approved rule
 * fits, reusing it is the faster and safer answer, so it is offered first; when none does, the
 * panel opens on the new-rule form and says plainly that nothing in the library fits.
 */
function ApplyPanel({
  item,
  datasetId,
  editable,
  headline,
  onClose,
}: {
  readonly item: Suggestion;
  readonly datasetId: string;
  readonly editable: readonly string[];
  readonly headline: string;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const matching = useMatchingRules(item.id, true);
  const apply = useApplySuggestion();
  const rules = matching.data?.rules ?? [];

  const [path, setPath] = useState<Path | null>(null);
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [severity, setSeverity] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [parameters, setParameters] = useState<Record<string, string>>(() =>
    Object.fromEntries(editable.map((key) => [key, item.parameters[key] ?? ''])),
  );
  const [requestActivation, setRequestActivation] = useState(true);

  /* Derived rather than stored, so the panel settles on the right path once the list arrives
     without an effect: a person's own choice, once made, always wins. */
  const effectivePath: Path = path ?? (rules.length > 0 ? 'existing' : 'new');
  const chosen: ExistingRule | undefined =
    rules.find((rule) => rule.rule_id === chosenId) ?? rules[0];
  const allowed = chosen === undefined ? [] : freeSeverities(chosen, SEVERITIES);
  const effectiveSeverity =
    severity !== null && allowed.includes(severity)
      ? severity
      : allowed.includes('warning')
        ? 'warning'
        : (allowed[0] ?? 'warning');
  const links = chosen?.coverage === 'covers_this';
  /* A template's blanks, still empty. The server refuses a draft with one empty; saying so beside
     the fields and holding the button is the same refusal without the round trip. */
  const unfilled = item.needs_input.filter((key) => !(parameters[key] ?? '').trim());
  const blocked = chosen !== undefined && !links && allowed.length === 0;

  const close = (): void => {
    apply.reset();
    onClose();
  };

  const submit = (): void => {
    if (effectivePath === 'existing' && chosen !== undefined) {
      apply.mutate(
        {
          suggestionId: item.id,
          datasetId,
          requestActivation: false,
          existingRuleId: chosen.rule_id,
          severity: effectiveSeverity,
        },
        { onSuccess: onClose },
      );
      return;
    }
    apply.mutate(
      {
        suggestionId: item.id,
        datasetId,
        name,
        ...(editable.length > 0 ? { parameters } : {}),
        requestActivation,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <form
      aria-label={t('suggestions.apply.form', { kind: item.kind })}
      className="lq-sg__editor"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="lq-sg__editor-head">
        <span className="lq-sg__eyebrow">{t('suggestions.apply.title')}</span>
        <span className="lq-sg__headline">{headline}</span>
      </div>

      <div
        className="lq-sg__paths"
        role="radiogroup"
        aria-label={t('suggestions.apply.how')}
      >
        <label
          className={`lq-sg__path${effectivePath === 'existing' ? ' lq-sg__path--on' : ''}${
            rules.length === 0 ? ' lq-sg__path--off' : ''
          }`}
        >
          <input
            checked={effectivePath === 'existing'}
            className="lq-sg__path-input"
            disabled={rules.length === 0}
            name={`path-${item.id}`}
            onChange={() => {
              setPath('existing');
            }}
            type="radio"
          />
          <span className="lq-sg__path-title">{t('suggestions.apply.reuse')}</span>
          <span className="lq-sg__path-note">
            {matching.isPending
              ? t('suggestions.apply.reuseLoading')
              : matching.isError
                ? t('suggestions.apply.reuseFailed')
                : rules.length === 0
                  ? t('suggestions.apply.reuseNone')
                  : t('suggestions.apply.reuseCount', { count: rules.length })}
          </span>
        </label>
        <label
          className={`lq-sg__path${effectivePath === 'new' ? ' lq-sg__path--on' : ''}`}
        >
          <input
            checked={effectivePath === 'new'}
            className="lq-sg__path-input"
            name={`path-${item.id}`}
            onChange={() => {
              setPath('new');
            }}
            type="radio"
          />
          <span className="lq-sg__path-title">{t('suggestions.apply.create')}</span>
          <span className="lq-sg__path-note">{t('suggestions.apply.createNote')}</span>
        </label>
      </div>

      {effectivePath === 'existing' && chosen !== undefined && (
        <>
          <fieldset className="lq-sg__rules">
            <legend className="lq-sg__eyebrow">{t('suggestions.reuse.pick')}</legend>
            {rules.map((rule) => (
              <label
                className={`lq-sg__ruleopt${
                  rule.rule_id === chosen.rule_id ? ' lq-sg__ruleopt--on' : ''
                }`}
                key={rule.rule_id}
              >
                <input
                  checked={rule.rule_id === chosen.rule_id}
                  className="lq-sg__path-input"
                  name={`rule-${item.id}`}
                  onChange={() => {
                    setChosenId(rule.rule_id);
                  }}
                  type="radio"
                />
                <span className="lq-sg__ruleopt-name">
                  {rule.name}
                  <span className="lq-sg__ruleopt-version">
                    {t('suggestions.reuse.version', { version: rule.version })}
                  </span>
                </span>
                <span className="lq-sg__ruleopt-meta">
                  {rule.columns.length === 0
                    ? t('suggestions.reuse.anyColumn')
                    : t('suggestions.reuse.forColumns', {
                        columns: columnsLabel(rule.columns),
                      })}
                  {Object.keys(rule.parameters).length > 0 &&
                    ` · ${parameterText(rule.parameters)}`}
                </span>
                <span
                  className={`lq-sg__ruleopt-coverage lq-sg__ruleopt-coverage--${rule.coverage}`}
                >
                  {t(coverageKey(rule), {
                    columns: columnsLabel(item.columns),
                    severities: rule.bound_severities
                      .map((used) => t(`suggestions.bind.severity.${used}`))
                      .join(', '),
                  })}
                </span>
              </label>
            ))}
          </fieldset>

          {!links && !blocked && (
            <label className="lq-sg__severity-field">
              <span className="lq-sg__eyebrow">{t('suggestions.bind.severity')}</span>
              <select
                className="lq-input lq-sg__severity"
                onChange={(event) => {
                  setSeverity(event.target.value);
                }}
                value={effectiveSeverity}
              >
                {allowed.map((choice) => (
                  <option key={choice} value={choice}>
                    {t(`suggestions.bind.severity.${choice}`)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <p className="lq-sg__note">
            {t(
              links
                ? 'suggestions.reuse.noteLink'
                : blocked
                  ? 'suggestions.reuse.noteBlocked'
                  : 'suggestions.reuse.note',
            )}
          </p>
        </>
      )}

      {effectivePath === 'new' && (
        <>
          <TextField
            label={t('suggestions.apply.name')}
            hint={t('suggestions.apply.nameHint')}
            maxLength={200}
            onChange={setName}
            value={name}
          />
          {editable.map((key) => (
            <TextField
              key={key}
              label={t(`suggestions.apply.parameter.${key}`, { defaultValue: key })}
              mono
              onChange={(value) => {
                setParameters((current) => ({ ...current, [key]: value }));
              }}
              value={parameters[key] ?? ''}
            />
          ))}
          {unfilled.length > 0 && (
            <p className="lq-sg__note" role="status">
              {t('suggestions.apply.templateMissing', { fields: unfilled.join(', ') })}
            </p>
          )}
          <CheckboxField
            checked={requestActivation}
            hint={t('suggestions.apply.activationHint')}
            label={t('suggestions.apply.activation')}
            onChange={setRequestActivation}
          />
          <p className="lq-sg__note">{t('suggestions.apply.note')}</p>
        </>
      )}

      {apply.error !== null && (
        <p className="lq-sg__row-error" role="alert">
          {apply.error.message}
        </p>
      )}

      <div className="lq-sg__editor-actions">
        <button
          className="lq-sg__go"
          disabled={
            apply.isPending ||
            (effectivePath === 'existing' && blocked) ||
            (effectivePath === 'new' && unfilled.length > 0)
          }
          type="submit"
        >
          {t(
            apply.isPending
              ? 'suggestions.apply.working'
              : effectivePath === 'new'
                ? 'suggestions.apply.submit'
                : links
                  ? 'suggestions.reuse.submitLink'
                  : 'suggestions.reuse.submit',
          )}
        </button>
        <button className="lq-sg__act lq-sg__act--quiet" onClick={close} type="button">
          {t('suggestions.act.cancel')}
        </button>
      </div>
    </form>
  );
}
